import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { renderToString } from "react-dom/server";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StaticRouter } from "react-router-dom/server.js";
import { I18nextProvider } from "react-i18next";
import * as helmetAsync from "react-helmet-async";
import { LRUCache } from "lru-cache";
const helmetModule = helmetAsync;
const HelmetProvider = helmetModule.HelmetProvider ?? helmetModule.default.HelmetProvider;
class RenderCache {
    cache = new LRUCache({
        max: 1000,
        ttl: 15 * 60 * 1000,
        updateAgeOnGet: true,
        allowStale: true,
    });
    stats = {
        hits: 0,
        misses: 0,
        sets: 0,
        errors: 0,
        componentCacheHits: 0,
        componentCacheMisses: 0,
    };
    get(url, userContext, routeParams) {
        const key = this.key(url, userContext, routeParams);
        const value = this.cache.get(key);
        if (value) {
            this.stats.hits++;
            return { ...value, key };
        }
        this.stats.misses++;
        return null;
    }
    set(url, html, userContext, routeParams) {
        const key = this.key(url, userContext, routeParams);
        const cacheType = url.includes("/dashboard") || url.includes("/profile")
            ? "user"
            : url.includes("/blog/")
                ? "dynamic"
                : "static";
        const ttl = cacheType === "user"
            ? 2 * 60 * 1000
            : cacheType === "dynamic"
                ? 5 * 60 * 1000
                : 30 * 60 * 1000;
        this.cache.set(key, { html, timestamp: Date.now(), cacheType, isSSR: true, metadata: {} }, { ttl });
        this.stats.sets++;
        return { key, type: cacheType, timestamp: Date.now(), ttl };
    }
    key(url, userContext, routeParams) {
        const params = Object.entries(routeParams)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => `${key}:${value}`)
            .join("|");
        const identity = userContext?.userId
            ? `::user:${userContext.userId}`
            : userContext?.sessionId
                ? `::session:${userContext.sessionId}`
                : "";
        return `${url}${params ? `::params:${params}` : ""}${identity}`;
    }
    clear() {
        this.cache.clear();
        Object.keys(this.stats).forEach((key) => {
            this.stats[key] = 0;
        });
    }
    invalidate(pattern) {
        const regex = new RegExp(pattern);
        let count = 0;
        for (const key of this.cache.keys())
            if (regex.test(key)) {
                this.cache.delete(key);
                count++;
            }
        return count;
    }
    getStats() {
        const total = this.stats.hits + this.stats.misses;
        return {
            ...this.stats,
            total,
            hitRate: total
                ? `${((this.stats.hits / total) * 100).toFixed(2)}%`
                : "0%",
            componentHitRate: "0%",
            size: this.cache.size,
            maxSize: this.cache.max,
            componentCacheSize: 0,
        };
    }
}
const routeMatches = (pathname, route) => {
    const names = [];
    const pattern = route
        .replace(/:(\w+)|\[(\w+)\]/g, (token) => {
        names.push(token.startsWith(":") ? token.slice(1) : token.slice(1, -1));
        return "([^/]+)";
    })
        .replace(/\//g, "\\/");
    const match = new RegExp(`^${pattern}$`).exec(pathname);
    return match
        ? {
            matches: true,
            params: Object.fromEntries(names.map((name, index) => [name, match[index + 1] ?? ""])),
        }
        : { matches: false, params: {} };
};
const shouldSSR = (pathname, routes, languages) => {
    let path = pathname.split("?")[0] || "/";
    const first = path.split("/").filter(Boolean)[0];
    if (first &&
        Object.keys(languages).some((language) => language.toLowerCase().startsWith(first.toLowerCase())))
        path = `/${path.split("/").filter(Boolean).slice(1).join("/")}`;
    const direct = routes[path] || routes[path.replace(/\/$/, "")];
    if (direct?.ssr === true)
        return true;
    return Object.entries(routes).some(([route, config]) => config.ssr === true &&
        (route.includes(":") || route.includes("[")) &&
        routeMatches(path, route).matches);
};
const extractPath = (url, languages, getFullLocaleCode) => {
    const pathname = new URL(url, "http://localhost").pathname;
    const parts = pathname.split("/").filter(Boolean);
    const candidate = parts[0];
    const isLanguage = candidate &&
        Object.keys(languages).some((item) => item.toLowerCase().startsWith(candidate.toLowerCase()));
    const localizedPath = `/${parts.slice(1).join("/")}`;
    return {
        language: isLanguage
            ? (getFullLocaleCode?.(candidate) ?? candidate)
            : "en-US",
        path: isLanguage ? localizedPath : pathname,
    };
};
const fetchRouteData = async (context, queryParams, routeParams, language) => {
    if (!context.config.entityService)
        return null;
    try {
        let serialized = JSON.stringify(queryParams);
        for (const [key, value] of Object.entries(routeParams))
            serialized = serialized.replace(new RegExp(`__${key.toUpperCase()}__`, "g"), value);
        serialized = serialized.replace(/__LANG_CODE__/g, language.replace("-", "_").toLowerCase());
        const params = JSON.parse(serialized);
        if (typeof params !== "object" ||
            params === null ||
            Array.isArray(params)) {
            return null;
        }
        return await context.config.entityService.query(params);
    }
    catch {
        return null;
    }
};
const getPageComponent = async (context, pathname, routeParams, language) => {
    const routes = context.config.publicRoutesConfig;
    let route = routes[pathname];
    let params = routeParams;
    if (!route)
        for (const [candidate, config] of Object.entries(routes)) {
            const matched = routeMatches(pathname, candidate);
            if (matched.matches) {
                route = config;
                params = matched.params;
                break;
            }
        }
    if (!route)
        return context.config.notFound
            ? React.createElement(context.config.notFound)
            : React.createElement("div", null, "Not Found");
    const data = route.queryParams
        ? await fetchRouteData(context, route.queryParams, params, language)
        : null;
    if (route.component)
        return React.createElement(route.component, {
            params,
            ssrData: data,
        });
    if (route.dynamicComponent) {
        const loaded = await route.dynamicComponent();
        return React.createElement(loaded.default, {
            params,
            ssrData: data,
        });
    }
    return context.config.notFound
        ? React.createElement(context.config.notFound)
        : React.createElement("div", null, "Not Found");
};
export const createSsrRenderer = (config) => {
    const context = {
        config,
        cache: new RenderCache(),
        queryClient: new QueryClient({
            defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: false } },
        }),
    };
    const getSSRRoutes = () => Object.entries(config.publicRoutesConfig)
        .filter(([, route]) => route.ssr === true)
        .flatMap(([route, value]) => {
        const paths = route.includes(":") || route.includes("[")
            ? (config.dynamicPathService?.getPaths(route, "en-US") ??
                value.paths ??
                [])
            : [route];
        return paths.map((path) => route.includes(":") || route.includes("[")
            ? route.replace(/(:[^/]+|\[[^\]]+\])/, path)
            : path);
    });
    const renderer = {
        async render(url, i18n, options = {}) {
            const { language, path } = extractPath(url, config.availableLanguages, config.getFullLocaleCode);
            if (!shouldSSR(path, config.publicRoutesConfig, config.availableLanguages))
                return null;
            const userContext = options.userContext ?? null;
            const routeParams = Object.entries(config.publicRoutesConfig)
                .map(([route]) => routeMatches(path, route))
                .find((match) => match.matches)?.params ?? {};
            if (options.useCache !== false) {
                const cached = context.cache.get(url, userContext, routeParams);
                if (cached)
                    return {
                        html: cached.html,
                        isSSR: true,
                        cached: true,
                        cacheInfo: {
                            type: cached.cacheType,
                            timestamp: cached.timestamp,
                            key: cached.key,
                            ttl: 0,
                        },
                    };
            }
            if (i18n.language !== language)
                await i18n.changeLanguage(language);
            let content = await getPageComponent(context, path, routeParams, language);
            if (options.AuthProvider)
                content = React.createElement(options.AuthProvider, null, content);
            if (options.TooltipProvider)
                content = React.createElement(options.TooltipProvider, null, content);
            if (options.ThemeProvider)
                content = React.createElement(options.ThemeProvider, null, content);
            const helmetContext = {};
            const html = renderToString(_jsx(HelmetProvider, { context: helmetContext, children: _jsx(StaticRouter, { location: url, children: _jsx(I18nextProvider, { i18n: i18n, children: _jsx(QueryClientProvider, { client: context.queryClient, children: content }) }) }) }));
            const cacheInfo = options.useCache === false
                ? undefined
                : context.cache.set(url, html, userContext, routeParams);
            return { html, isSSR: true, cached: false, cacheInfo };
        },
        getSSRRoutes,
        async warmCache(i18nCreator) {
            await renderer.initializeDynamicPathService();
            for (const route of getSSRRoutes()) {
                const { i18n } = await i18nCreator(route);
                await renderer.render(route, i18n);
            }
        },
        clearCache() {
            context.cache.clear();
        },
        invalidateCache(pattern) {
            return context.cache.invalidate(pattern);
        },
        getCacheStats() {
            return context.cache.getStats();
        },
        renderSitemap(baseUrl = "https://emotion.guru") {
            const urls = getSSRRoutes()
                .map((route) => `<url><loc>${baseUrl}${route}</loc></url>`)
                .join("\n");
            return `<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">${urls}</urlset>`;
        },
        async initializeDynamicPathService() {
            await config.dynamicPathService?.initialize?.();
        },
        getDynamicPathStats() {
            return config.dynamicPathService?.getStats() ?? null;
        },
        async triggerPathUpdate(route, language) {
            return ((await config.dynamicPathService?.triggerUpdate(route, language)) ?? []);
        },
        getDynamicPaths(route, language = "en-US") {
            return config.dynamicPathService?.getPaths(route, language) ?? [];
        },
        getAllDynamicPaths(route) {
            return config.dynamicPathService?.getAllPaths(route) ?? new Map();
        },
    };
    return renderer;
};
//# sourceMappingURL=entry-server.js.map